/**
 * Demand Service
 * All demand-related API calls
 */

import { api } from '../../../shared/services/apiClient';

const demandService = {
  /**
   * Get demand details by ID (including screen assignments)
   */
  getDemandDetails: async (demandID) => {
    const response = await api.get(`/DemandRequest/GetDemandDetails?DemandID=${demandID}`);
    return response.data;
  },

  /**
   * Get demand list
   */
  getDemandList: async (employeeID, roleName, companyID) => {
    const response = await api.get(
      `/DemandRequest/GetDemandList?EmployeeID=${employeeID}&RoleName=${roleName}&CompanyID=${companyID}`
    );
    return response.data;
  },

  /**
   * Get demand list (paged + search)
   */
  getDemandListPaged: async (employeeID, roleName, companyID, page = 0, size = 10, searchText = '') => {
    const response = await api.get('/DemandRequest/GetDemandListPaged', {
      params: { EmployeeID: employeeID, RoleName: roleName, CompanyID: companyID, page, size, searchText }
    });
    return response.data;
  },

  /**
   * Create new demand
   */
  createDemand: async (demandData) => {
    const response = await api.post('/DemandRequest/CreateDemand', demandData);
    return response.data;
  },

  /**
   * Update demand
   */
  updateDemand: async (demandData) => {
    const response = await api.post('/DemandRequest/UpdatedemandData', demandData);
    return response.data;
  },

  /**
   * Delete demand
   */
  deleteDemand: async (demandID) => {
    const response = await api.get(`/DemandRequest/DeleteDemand?DemandID=${demandID}`);
    return response.data;
  },

  /**
   * Change demand status
   */
  changeDemandStatus: async (demandStatus, demandId) => {
    const response = await api.get(
      `/DemandRequest/ChangeDemandStatus?DemandStatus=${demandStatus}&demandId=${demandId}`
    );
    return response.data;
  },

  /**
   * Get dropdown data
   */
  getDropdownData: async (code) => {
    const response = await api.get(`/Settings/DropDownList?Code=${code}`);
    return response.data;
  },

  /**
   * Get employee list
   */
  getEmployeeList: async () => {
    const response = await api.get('/Employee/List');
    return response.data;
  },

  /**
   * Get profile mappers
   */
  getProfileMappers: async (companyID, roleName) => {
    const response = await api.get(
      `/DemandRequest/ProfileMappers?CompanyID=${companyID}&roleName=${roleName}`
    );
    return response.data;
  },

  /**
   * Get screening levels
   */
  getScreeningLevels: async () => {
    const response = await api.get('/DemandRequest/ScreeningLevels');
    return response.data;
  },

  /**
   * Get screen base employees
   */
  getScreenBaseEmployee: async (companyID, roleName) => {
    const response = await api.get(
      `/DemandRequest/ScreenBaseEmployee?CompanyID=${companyID}&roleName=${roleName}`
    );
    return response.data;
  },

  /**
   * Download template
   */
  downloadTemplate: async (companyID, roleName) => {
    const response = await api.get(
      `/DemandRequest/TemplateList?companyID=${companyID}&roleName=${roleName}`,
      { responseType: 'arraybuffer' }
    );
    return response.data;
  },

  /**
   * Accept assessment
   */
  acceptAssessment: async (demandId, step) => {
    const response = await api.get(
      `/DemandRequest/AcceptAssessment?DemandId=${demandId}&Step=${step}`
    );
    return response.data;
  },

  /**
   * Save SLA accept time
   */
  saveSLAAcceptTime: async (demandId, step, empID) => {
    const response = await api.get(
      `/DemandRequest/SLA_SaveAcceptTime?DemandId=${demandId}&Step=${step}&EmpID=${empID}`
    );
    return response.data;
  },

  /**
   * Send email on assignee change
   */
  sendEmailOnAssigneeChange: async (emailId, demandID) => {
    const response = await api.get(
      `/Login/SendEmailOnAssigneeChange?AssociateEmail=${emailId}&demandId=${demandID}`
    );
    return response.data;
  },

  /**
   * Get candidates by demand ID
   */
  getCandidatesByDemandId: async (demandID, employeeId, roleName) => {
    const response = await api.get(
      `/DemandRequest/GetCandidateProfileByDemandID?DemandID=${demandID}&EmployeeId=${employeeId}&RoleName=${roleName}`
    );
    return response.data;
  },

  /**
   * Move candidate forward
   */
  moveForward: async (demandId, stepName, demandStep, profileLevel, employeeID, demandRID, sla) => {
    const response = await api.get(
      `/DemandRequest/MoveForward?DemandId=${demandId}&StepName=${stepName}&DemandStep=${demandStep}&ProfileLevel=${profileLevel}&EmployeeID=${employeeID}&DemandRID=${demandRID}&Sla=${sla}`
    );
    return response.data;
  },
  moveForward: async (demandId, employeeID) => {
    const response = await api.get(
      `/DemandRequest/MoveForward1?DemandId=${demandId}&EmployeeID=${employeeID}`
    );
    return response.data;
  },

  claimCandidate: async (demandCandidateId, employeeID) => {
    const response = await api.post(
      `/DemandRequest/ClaimCandidate?DemandCandidateId=${demandCandidateId}&EmployeeID=${employeeID}`
    );
    return response.data;
  },

  releaseCandidate: async (demandCandidateId, employeeID) => {
    const response = await api.post(
      `/DemandRequest/ReleaseCandidate?DemandCandidateId=${demandCandidateId}&EmployeeID=${employeeID}`
    );
    return response.data;
  },
  /**
   * Move candidate backward
   */
  moveBackward: async (demandId, employeeID, comment, targetStepId = null) => {
    let url = `/DemandRequest/MoveBack?DemandId=${demandId}&EmployeeID=${employeeID}&comment=${encodeURIComponent(comment)}`;
    if (targetStepId !== null && targetStepId !== undefined && targetStepId !== '') {
      url += `&targetStepId=${encodeURIComponent(targetStepId)}`;
    }
    const response = await api.get(url);
    return response.data;
  },

  /**
   * Reject candidate
   */
  rejectCandidate: async (demandId, demandStep, reasonId, comments, loggedInEmployeeId) => {
    const params = new URLSearchParams({
      DemandId: demandId,
      DemandStep: demandStep,
      ReasonId: reasonId,
      EmployeeId: loggedInEmployeeId
    });

    if (comments) {
      params.append('Comments', comments);
    }

    const response = await api.get(`/DemandRequest/Reject?${params.toString()}`);
    return response.data;
  },

  /**
   * Map candidate to demand
   */
  candidateMapping: async (candidateID, demandID) => {
    const response = await api.get(
      `/DemandRequest/CandidateMapping?CandidateID=${candidateID}&DemandID=${demandID}`
    );
    return response.data;
  },

  checkPanNumber: async (panNumber, candidateID) => {
    const response = await api.get(`/DemandRequest/checkpan?panNumber=${panNumber}&candidateId=${candidateID}`);
    return response.data;
  },

  /**
   * Create or update candidate
   * Note: Resume is sent as base64 in FileUploadpath field
   */
  createCandidate: async (candidateData) => {
    const response = await api.post('/DemandRequest/CreateCandidate', candidateData);
    return response.data;
  },

  /**
   * Delete candidate
   */
  deleteCandidate: async (demandCandidateID) => {
    const response = await api.get(`/DemandRequest/DeleteCandidate?candidateID=${demandCandidateID}`);
    return response.data;
  },

  /**
   * Get notifications
   */
  getNotifications: async (empID) => {
    const response = await api.get(`/DemandRequest/GetNotification?EmpID=${empID}`);
    return response.data;
  },

  /**
   * Get notification count
   */
  getNotificationCount: async (empID) => {
    const timestamp = Date.now();
    const response = await api.get(`/DemandRequest/GetCnt?EmpID=${empID}&_t=${timestamp}`);
    return response.data;
  },

  /**
   * Clear notification count
   */
  clearNotificationCount: async (empID) => {
    const timestamp = Date.now();
    const response = await api.get(`/DemandRequest/ClearCnt?EmpID=${empID}&_t=${timestamp}`);
    return response.data;
  },

  /**
   * Clear all notifications
   */
  clearAllNotifications: async (empID) => {
    const timestamp = Date.now();
    const response = await api.get(`/DemandRequest/ClearAll?EmpID=${empID}&_t=${timestamp}`);
    return response.data;
  },

  /**
   * Clear single notification
   */
  clearSingleNotification: async (id) => {
    const response = await api.get(`/DemandRequest/SingleClear?Id=${id}`);
    return response.data;
  },

  /**
   * Mark notification as read
   */
  markAsRead: async (id) => {
    const response = await api.get(`/DemandRequest/MarkAsRead?Id=${id}`);
    return response.data;
  },

  /**
   * SLA Accept Screen
   */
  slaAcceptScreen: async (arrString, step) => {
    const response = await api.get(
      `/DemandRequest/SLA_AcceptScreen?ArrString=${arrString}&Step=${step}`
    );
    return response.data;
  },

  /**
   * Get employee list
   */
  getEmployees: async () => {
    const response = await api.get('/DemandRequest/EmployeeList');
    return response.data;
  },

  /**
   * Get skill set ID
   */
  getSkillSetId: async (skillSet) => {
    const response = await api.get(`/DemandRequest/GetSkillSetId?SkillSet=${skillSet}`);
    return response.data;
  },

  /**
   * Download Excel file
   */
  downloadExcel: async () => {
    const response = await api.get('/DemandRequest/ExcelDownload', {
      responseType: 'arraybuffer'
    });
    return response.data;
  },

  /**
   * Get locations list
   * Uses Settings API instead of dropdown
   */
  getLocations: async () => {
    const response = await api.get('/Settings/GetLocationDetails');
    return response.data;
  },

  /**
   * Download candidate resume (legacy - for backward compatibility)
   */
  downloadResume: async (candidateID) => {
    const response = await api.post('/CandidatePipeLine/fileSave', {
      CandidateID: Number(candidateID)
    });
    return response.data;
  },

  /**
   * Download candidate resume from Google Cloud Storage
   * Returns the resume file as a blob with filename from headers
   */
  downloadResumeFromGCS: async (candidateID) => {
    // First, try to get the filename from the signed URL endpoint
    let fileName = null;
    try {
      const urlResponse = await api.get(`/candidate/${candidateID}/resume/url?expirationMinutes=1`);
      if (urlResponse.data && urlResponse.data.fileName) {
        fileName = urlResponse.data.fileName;
      }
    } catch (urlError) {
      console.warn('Could not get filename from URL endpoint:', urlError);
    }

    const response = await api.get(`/candidate/${candidateID}/resume/download`, {
      responseType: 'blob'
    });

    // Extract filename from Content-Disposition header as backup
    if (!fileName) {
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (fileNameMatch && fileNameMatch[1]) {
          fileName = fileNameMatch[1].replace(/['"]/g, '');
        }
      }
    }

    return {
      blob: response.data,
      fileName: fileName
    };
  },

  /**
   * Get signed URL for resume download from Google Cloud Storage
   * Returns a temporary URL that can be used to download the resume
   */
  getResumeSignedUrl: async (candidateID, expirationMinutes = 60) => {
    const response = await api.get(
      `/candidate/${candidateID}/resume/url?expirationMinutes=${expirationMinutes}`
    );
    return response.data;
  },

  /**
   * Get candidate notifications
   */
  getCandidateNotifications: async (empID, companyID) => {
    const response = await api.get(
      `/CandidatePipeLine/GetNotification?EmpID=${empID}&CompanyID=${companyID}`
    );
    return response.data;
  },

  /**
   * Get mandatory fields configuration
   */
  getMandatoryFields: async () => {
    const response = await api.get('/ApplicationConfig/GetFormMandatoryField');
    return response.data;
  },

  /**
   * Save dropdown configuration (for adding new skillsets, etc.)
   * Uses the same API as Application Configuration
   */
  saveDropdownConfig: async (configData) => {
    const response = await api.post('/Settings/DropDownListSave', configData);
    return response.data;
  },

  /**
   * Check if dropdown value already exists
   */
  checkDropdownExists: async (configData) => {
    const response = await api.post('/Settings/Getcheckexitsornot', configData);
    return response.data;
  },

  /**
   * Get candidate historical comments
   */
  getCandidateComments: async (demandCandidateId) => {
    const response = await api.get(`/DemandRequest/GetComments?demandCandidateId=${demandCandidateId}`);
    return response.data;
  },
};

export default demandService;

